import React, { useState, useEffect } from 'react';
import { renderContent } from '../renderContent';
import { apiClient } from '../../utils/apiClient';
import { useBandeau } from '../BandeauContext';

function FlashcardSession({ cards, user, onExit, preview = false }) {
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [notification, setNotification] = useState(null);
    const [sessionStats, setSessionStats] = useState({ facile: 0, moyen: 0, difficile: 0 });
    const { setBandeau, resetBandeau } = useBandeau();

    useEffect(() => {
        if (preview) return;
        setBandeau({ title: 'Flashcards', breadcrumb: [{ label: 'Accueil', onClick: onExit }] });
        return () => resetBandeau();
    }, [preview, setBandeau, resetBandeau, onExit]);

    const handleResponse = (niveau) => {
        const currentCard = cards[currentCardIndex];

        apiClient(`${process.env.REACT_APP_API_BASE_URL}/save_flashcard_review.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ student_id: user.id, flashcard_id: currentCard.id, rating: niveau })
        })
        .then(async res => {
            const text = await res.text();
            try { return JSON.parse(text); } catch (e) { throw new Error("Le serveur a renvoyé une erreur non-JSON."); }
        })
        .then(data => {
            if (data.success) {
                const key = niveau === 2 ? 'facile' : niveau === 1 ? 'moyen' : 'difficile';
                const newStats = { ...sessionStats, [key]: sessionStats[key] + 1 };
                setSessionStats(newStats);

                if (currentCardIndex + 1 < cards.length) {
                    setIsFlipped(false);
                    setCurrentCardIndex(currentCardIndex + 1);
                } else {
                    setNotification({ type: 'success', text: "Session terminée !" });
                    setTimeout(() => { onExit(newStats); }, 2500);
                }
            } else {
                setNotification({ type: 'error', text: "Erreur : " + data.message });
                setTimeout(() => setNotification(null), 4000);
            }
        })
        .catch(err => {
            setNotification({ type: 'error', text: err.message });
            setTimeout(() => setNotification(null), 4000);
        });
    };

    const currentCard = cards[currentCardIndex];

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in relative z-10 text-gray-800 dark:text-gray-200">
            {!preview && (
                <div className="w-full max-w-2xl mb-8">
                    <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2">
                        <span>Progression</span>
                        <span>Carte {currentCardIndex + 1} sur {cards.length}</span>
                    </div>
                    <div className="w-full bg-white/[0.05] rounded-full h-2 overflow-hidden">
                        <div className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${((currentCardIndex + 1) / cards.length) * 100}%` }}></div>
                    </div>
                </div>
            )}

            <div onClick={() => setIsFlipped(!isFlipped)} className="group w-full max-w-2xl min-h-[300px] bg-white/70 dark:bg-white/[0.03] backdrop-blur-2xl rounded-3xl p-10 border border-gray-200 dark:border-white/[0.08] hover:border-fuchsia-500/50 shadow-[0_0_50px_rgba(217,70,239,0.1)] hover:shadow-[0_0_50px_rgba(217,70,239,0.2)] transition-all duration-500 cursor-pointer flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/5 to-cyan-500/5 pointer-events-none"></div>
                <p className="text-xs uppercase tracking-widest text-fuchsia-600 dark:text-fuchsia-400 font-bold mb-6 text-center z-10">
                    {isFlipped ? "Réponse" : (currentCard.category && currentCard.category.split('>').map((cat, i, arr) => (<React.Fragment key={i}><span>{cat.trim()}</span>{i < arr.length - 1 && <span className="mx-2 opacity-50">❯</span>}</React.Fragment>)))}
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white text-center leading-relaxed z-10 w-full">
                    {renderContent(isFlipped ? currentCard.answer : currentCard.question)}
                </h2>
                {!isFlipped && <p className="absolute bottom-6 text-gray-500 dark:text-gray-500 text-sm animate-pulse z-10">Clique pour voir la réponse</p>}
            </div>

            {!preview && (
                <div className={`mt-12 flex flex-wrap justify-center gap-4 transition-all duration-500 ${isFlipped && !(notification && notification.type === 'success') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                    <button onClick={() => handleResponse(0)} className="px-8 py-3 rounded-xl font-bold text-red-700 dark:text-white bg-red-500/10 border border-red-500/50 hover:bg-red-500/20 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] transition-all">Difficile 🥵</button>
                    <button onClick={() => handleResponse(1)} className="px-8 py-3 rounded-xl font-bold text-orange-700 dark:text-white bg-orange-500/10 border border-orange-500/50 hover:bg-orange-500/20 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] transition-all">Moyen 😐</button>
                    <button onClick={() => handleResponse(2)} className="px-8 py-3 rounded-xl font-bold text-emerald-700 dark:text-white bg-emerald-500/10 border border-emerald-500/50 hover:bg-emerald-500/20 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all">Facile 🤩</button>
                </div>
            )}

            {notification && (
                <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl font-bold text-white shadow-[0_0_30px_rgba(0,0,0,0.5)] transition-all z-50 animate-fade-in ${notification.type === 'success' ? 'bg-emerald-500/90 border border-emerald-400' : 'bg-red-500/90 border border-red-400'}`}>
                    {notification.text}
                </div>
            )}
        </div>
    );
}

export default FlashcardSession;